// React
import { useState, useEffect } from 'react'

// Interfaces
import { IRoleFormHookProps, IRoleFormHookAccessFields } from './interfaces'
import { IBaseForm } from 'interfaces/form'
import { IUserAccess } from 'interfaces/back-office/user-access'

// Utils
import { formUtils_mapFields } from 'utils/form/form.utils'

// Constant
import { FORM_INPUT } from 'constant/form.constant'
import { STATUS } from 'constant/shared.constant'
import {
  ACCESS_FEATURE_NAME,
  ACCESS_BACK_OFFICE_PERSONAL_DATA,
  ACCESS_BACK_OFFICE_LIST_OF_ASSESSMENT,
  ACCESS_BACK_OFFICE_USER_MANAGEMENT,
  ACCESS_BACK_OFFICE_CONTENT_MANAGEMENT,
  ACCESS_BACK_OFFICE_QA,
  ACCESS_BACK_OFFICE_MASTER_DATA,
  ACCESS_CODE_PERSONAL_DATA,
  ACCESS_CODE_USER_MANAGEMENT,
  ACCESS_CODE_CONTENT_MANAGEMENT,
  ACCESS_CODE_QA,
  ACCESS_CODE_MASTER_DATA,
  ACCESS_BACK_OFFICE_USER_ACCESS,
  ACCESS_CODE_USER_ACCESS,
  ACCESS_CODE_LIST_OF_ASSESSMENT,
} from 'constant/access.constant'

// i18n
import { useTranslation } from 'react-i18next'

const useMasterRoleForm = (props?: IRoleFormHookProps) => {
  // Hook
  const { t } = useTranslation()
  const [fields, setFields] = useState<IBaseForm[]>([
    {
      label: { label: 'masterRole.form.name', breakpoint: 6, layout: 'horizontal' },
      input: {
        inputName: FORM_INPUT.TEXT_FIELD,
        formName: 'name',
        breakpoint: 12,
        rules: ['required'],
        inputAttrs: {
          placeholder: 'masterRole.formPlaceholder.name',
        },
      },
    },
  ])
  const [secondFields, setSecondFields] = useState<IBaseForm[]>([
    {
      label: { label: 'masterRole.form.status', breakpoint: 6, layout: 'horizontal' },
      input: {
        inputName: FORM_INPUT.SELECT_OPTION,
        formName: 'status',
        breakpoint: 12,
        rules: ['required'],
        selectOptionAttrs: {
          placeholder: 'masterRole.formPlaceholder.status',
          list: STATUS.map(status => ({ ...status, label: t(status.label) })),
        },
      },
    },
  ])
  const [accessFields, setAccessFields] = useState<IRoleFormHookAccessFields[]>([
    // {
    //   parentFeatureName: ACCESS_FEATURE_NAME.WEBPAGE,
    //   parentFeatureCode: ACCESS_CODE_WEBPAGE[0],
    //   childrenFeatures: [],
    // },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.PERSONAL_DATA,
      parentFeatureCode: ACCESS_CODE_PERSONAL_DATA[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.LIST_OF_ASSESSMENT,
      parentFeatureCode: ACCESS_CODE_LIST_OF_ASSESSMENT[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.USER_MANAGEMENT,
      parentFeatureCode: ACCESS_CODE_USER_MANAGEMENT[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.CONTENT_MANAGEMENT,
      parentFeatureCode: ACCESS_CODE_CONTENT_MANAGEMENT[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.QA,
      parentFeatureCode: ACCESS_CODE_QA[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.MASTER_DATA,
      parentFeatureCode: ACCESS_CODE_MASTER_DATA[0],
      childrenFeatures: [],
    },
    {
      parentFeatureName: ACCESS_FEATURE_NAME.USER_ACCESS,
      parentFeatureCode: ACCESS_CODE_USER_ACCESS[0],
      childrenFeatures: [],
    },
  ])

  /**
   * @description Set access childrenFeatures according to feature name
   *
   * @param {IUserAccess[]} parentFeatureName
   *
   * @return {void} void
   */
  const mapAccessFields = (parentFeatureName: string): IUserAccess[] => {
    switch (parentFeatureName) {
      // case ACCESS_FEATURE_NAME.WEBPAGE:
      //   return ACCESS_WEBPAGE
      case ACCESS_FEATURE_NAME.PERSONAL_DATA:
        return ACCESS_BACK_OFFICE_PERSONAL_DATA
      case ACCESS_FEATURE_NAME.LIST_OF_ASSESSMENT:
        return ACCESS_BACK_OFFICE_LIST_OF_ASSESSMENT
      case ACCESS_FEATURE_NAME.USER_MANAGEMENT:
        return ACCESS_BACK_OFFICE_USER_MANAGEMENT
      case ACCESS_FEATURE_NAME.CONTENT_MANAGEMENT:
        return ACCESS_BACK_OFFICE_CONTENT_MANAGEMENT
      case ACCESS_FEATURE_NAME.QA:
        return ACCESS_BACK_OFFICE_QA
      case ACCESS_FEATURE_NAME.MASTER_DATA:
        return ACCESS_BACK_OFFICE_MASTER_DATA
      case ACCESS_FEATURE_NAME.USER_ACCESS:
        return ACCESS_BACK_OFFICE_USER_ACCESS
      default:
        return []
    }
  }

  /**
   * @description Map access fields
   *
   * @return {IRoleFormHookAccessFields[]} IRoleFormHookAccessFields[]
   */
  const remapAccessFields = (): IRoleFormHookAccessFields[] => {
    return accessFields.map(field => {
      return {
        ...field,
        childrenFeatures: mapAccessFields(field.parentFeatureName)
          .filter(fieldItem => !fieldItem.name.includes('Entry'))
          .map(fieldItem => {
            return { value: fieldItem.code, checkboxLabel: fieldItem.name }
          }),
      }
    }) as IRoleFormHookAccessFields[]
  }

  useEffect(() => {
    setFields(formUtils_mapFields(fields, []))

    setSecondFields(formUtils_mapFields(secondFields, []))

    setAccessFields(remapAccessFields())

    // eslint-disable-next-line
  }, [])

  return { fields, secondFields, accessFields }
}

export { useMasterRoleForm }
